import axiosClient from "./apiClient";
import { pollMqttEvents, extractReqId } from "./mqttEvents";
import {
  extractDeviceState,
  extractAlarms,
  hottestBurnerTempC,
  stovePoweredOnFromAlarms,
} from "./cookingState";
import { injectMockBlockedDeviceStatus } from "./devMocks";

export type DeviceStatus = {
  alarms: any | null;
  hottestTempC: number | null;
  stovePoweredOn: boolean | null;
};

// Live device state via an MQTT command/response round-trip:
// GetDeviceStatus → req_id → poll GetMqttEvents until a reply carrying an
// alarms object arrives. The `t` nonce busts any upstream HTTP/CDN cache so a
// recycled req_id can't return a stale reply. Returns null when no reply
// carries a recognisable device state (caller treats as "unknown").
export async function getDeviceStatus(
  name: string
): Promise<DeviceStatus | null> {
  const res = await axiosClient.get(`sensors/${name}/GetDeviceStatus`, {
    params: { t: Date.now() },
  });
  const reqId = extractReqId(res.data);
  if (!reqId) throw new Error("GetDeviceStatus response missing req_id");

  const events = await pollMqttEvents(name, reqId, {
    intervalMs: 1000,
    timeoutMs: 5000,
    readyWhen: (evs) => evs.some((e) => extractAlarms(e) != null),
  });

  for (const event of events) {
    const ds = extractDeviceState(event);
    if (ds) {
      const alarms =
        ds.alarms && typeof ds.alarms === "object" ? ds.alarms : null;
      // No-op unless REACT_APP_MOCK_BLOCKED_SENSOR is set (dev preview only).
      return injectMockBlockedDeviceStatus(name, {
        alarms,
        hottestTempC: hottestBurnerTempC(ds),
        stovePoweredOn: stovePoweredOnFromAlarms(alarms),
      });
    }
  }
  return injectMockBlockedDeviceStatus(name, null);
}
